import { useEffect, useState } from 'react';
import { Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/lib/auth-client';
import { annotations as api, type Annotation } from '@/lib/api';
import { useAnnotationsStore } from './store';

/**
 * F5.7 — Personal text annotations layer.
 *
 * Only renders for signed-in users (the server gates the list endpoint).
 * Click handling:
 *   - When `placing` is true (toolbar in note-drop mode), the next map
 *     click is captured into `pendingPlacement` and renders an inline
 *     editor popup at that location.
 *   - Existing annotations show as yellow sticky-note icons; their popup
 *     contains the text and a Delete button.
 */

function makeNoteIcon(): L.DivIcon {
  return L.divIcon({
    html: `<div style="
      display: flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 3px;
      background: #fde68a;
      color: #78350f;
      font-size: 12px;
      font-weight: 700;
      border: 1.5px solid #ca8a04;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    ">📝</div>`,
    className: '',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

const NOTE_ICON = makeNoteIcon();

export function AnnotationsLayer() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const qc = useQueryClient();

  const placing = useAnnotationsStore((s) => s.placing);
  const pending = useAnnotationsStore((s) => s.pendingPlacement);
  const capture = useAnnotationsStore((s) => s.capturePlacement);
  const clearPending = useAnnotationsStore((s) => s.clearPendingPlacement);

  // Bump map cursor while placing
  const map = useMapEvents({
    click(e) {
      if (placing) capture(e.latlng.lat, e.latlng.lng);
    },
  });

  useEffect(() => {
    const el = map.getContainer();
    el.style.cursor = placing ? 'crosshair' : '';
    return () => {
      el.style.cursor = '';
    };
  }, [map, placing]);

  const { data: items = [] } = useQuery({
    queryKey: ['annotations'],
    queryFn: () => api.list(),
    enabled: !!session,
    staleTime: 30 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (body: { lat: number; lon: number; text: string }) =>
      api.create({
        type: 'note',
        lat: body.lat,
        lon: body.lon,
        text: body.text,
        color: null,
        geometry: null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['annotations'] });
      clearPending();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['annotations'] }),
  });

  if (!session) return null;

  return (
    <>
      {items.filter(validCoords).map((a) => (
        <Marker key={a.id} position={[a.lat, a.lon]} icon={NOTE_ICON}>
          <Popup>
            <div className="max-w-[240px] space-y-1 text-xs">
              <div className="whitespace-pre-wrap text-slate-800">{a.text}</div>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('annotations.deleteConfirm', { defaultValue: 'Delete this note?' }))) {
                    deleteMutation.mutate(a.id);
                  }
                }}
                disabled={deleteMutation.isPending}
                className="mt-1 text-[10px] text-red-600 underline hover:text-red-700 disabled:opacity-50"
              >
                {t('annotations.delete', { defaultValue: 'Delete' })}
              </button>
            </div>
          </Popup>
        </Marker>
      ))}

      {/* Inline editor at click location */}
      {pending && (
        <Marker position={[pending.lat, pending.lon]} icon={NOTE_ICON}>
          <Popup
            autoClose={false}
            closeOnClick={false}
            eventHandlers={{ remove: clearPending }}
          >
            <NoteEditor
              onSave={(text) =>
                createMutation.mutate({ lat: pending.lat, lon: pending.lon, text })
              }
              onCancel={clearPending}
              busy={createMutation.isPending}
              t={t}
            />
          </Popup>
        </Marker>
      )}
    </>
  );
}

function NoteEditor({
  onSave,
  onCancel,
  busy,
  t,
}: {
  onSave: (text: string) => void;
  onCancel: () => void;
  busy: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [text, setText] = useState('');
  return (
    <div className="space-y-2 text-xs">
      <textarea
        // eslint-disable-next-line jsx-a11y/no-autofocus -- focus trap for accessibility: dialog input should receive focus on open
        autoFocus
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('annotations.placeholder', { defaultValue: 'Your note…' })}
        maxLength={2000}
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-[color:var(--color-terra-600)] focus:outline-none"
      />
      <div className="flex justify-end gap-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:bg-slate-50"
        >
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </button>
        <button
          type="button"
          onClick={() => text.trim() && onSave(text.trim())}
          disabled={busy || !text.trim()}
          className="rounded bg-[color:var(--color-terra-600)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] disabled:opacity-50"
        >
          {busy
            ? t('annotations.saving', { defaultValue: 'Saving…' })
            : t('annotations.save', { defaultValue: 'Save' })}
        </button>
      </div>
    </div>
  );
}

function validCoords(a: Annotation): boolean {
  return (
    typeof a.lat === 'number' &&
    typeof a.lon === 'number' &&
    a.lat >= 36 &&
    a.lat <= 48 &&
    a.lon >= 6 &&
    a.lon <= 19
  );
}
